package fyi.moko.hotpl.ui.util

import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.daysUntil
import kotlinx.datetime.monthsUntil
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.yearsUntil
import kotlin.time.Instant

/**
 * Turns the raw visit counters into the app's "temperature, not a feature" voice
 * (PROJECT.md §4-⑥): the numbers are already stored — this only changes how they
 * *sound*, deterministically, with no new data model. Kept as pure functions so
 * the tone is judged in isolation before any UI depends on it.
 *
 * English to match the shipping strings (`formatDate`); swapping the language is a
 * localized change confined to this file.
 */

/** "Visited once" / "Visited twice" / "Visited N times". */
fun visitCountPhrase(count: Int): String = when (count) {
    1 -> "Visited once"
    2 -> "Visited twice"
    else -> "Visited $count times"
}

/** Relative recency of the last visit, e.g. "yesterday", "3 weeks ago", "a year ago". */
fun lastVisitPhrase(
    lastVisitedAt: Long,
    now: Long,
    zone: TimeZone = TimeZone.currentSystemDefault(),
): String {
    val past = localDate(lastVisitedAt, zone)
    val today = localDate(now, zone)
    val days = past.daysUntil(today)
    val months = past.monthsUntil(today)
    val years = past.yearsUntil(today)
    return when {
        days <= 0 -> "today"
        days == 1 -> "yesterday"
        months < 1 && days < 7 -> "$days days ago"
        months < 1 -> (days / 7).let { if (it == 1) "a week ago" else "$it weeks ago" }
        years < 1 -> if (months == 1) "a month ago" else "$months months ago"
        years == 1 -> "a year ago"
        else -> "$years years ago"
    }
}

/**
 * True when [pastVisitedAt] lands on the same month/day as [now] in an earlier year —
 * the "you were here a year ago today" recall cue. Leap-day (Feb 29) matches only
 * on actual Feb 29s, which is acceptable for a soft cue.
 */
fun isAnniversaryToday(
    pastVisitedAt: Long,
    now: Long,
    zone: TimeZone = TimeZone.currentSystemDefault(),
): Boolean {
    val past = localDate(pastVisitedAt, zone)
    val today = localDate(now, zone)
    return past.year < today.year &&
        past.month == today.month &&
        past.day == today.day
}

/**
 * The lead line for the "you were here on this day" recall cue, or null when
 * [pastVisitedAt] is not an anniversary of [now]. The caller composes the place
 * ("$lead, you were at Blue Bottle") and shows it only on app-open — null means
 * show nothing (no empty banner). Deterministic: driven by the date, never random.
 */
fun anniversaryRecall(
    pastVisitedAt: Long,
    now: Long,
    zone: TimeZone = TimeZone.currentSystemDefault(),
): String? {
    if (!isAnniversaryToday(pastVisitedAt, now, zone)) return null
    val years = localDate(pastVisitedAt, zone).yearsUntil(localDate(now, zone))
    return if (years == 1) "A year ago today" else "$years years ago today"
}

/**
 * Day-count milestones for [recallLine], mapped to their phrasing. These are the
 * cross-cultural "reconsolidation" intervals from RECALL_INTERVALS_RESEARCH.md
 * (49 days = 7 weeks, 100 days). Kept as data — not hard-coded control flow — so a
 * future per-user set can be swapped in without touching the logic.
 */
val DEFAULT_DAY_MILESTONES: Map<Int, String> = mapOf(
    49 to "7 weeks ago today",
    100 to "100 days ago today",
)

/**
 * The recall lead line for [pastVisitedAt] relative to [now], or null when today
 * hits no milestone (→ show nothing). Yearly anniversaries take precedence over the
 * day milestones — a full year is the most iconic cue; the day deltas never coincide
 * with it. Deterministic, driven only by the date.
 */
fun recallLine(
    pastVisitedAt: Long,
    now: Long,
    zone: TimeZone = TimeZone.currentSystemDefault(),
    dayMilestones: Map<Int, String> = DEFAULT_DAY_MILESTONES,
): String? {
    anniversaryRecall(pastVisitedAt, now, zone)?.let { return it }
    val days = localDate(pastVisitedAt, zone).daysUntil(localDate(now, zone))
    return dayMilestones[days]
}

private fun localDate(epochMillis: Long, zone: TimeZone): LocalDate =
    Instant.fromEpochMilliseconds(epochMillis).toLocalDateTime(zone).date
